import React from "react";
import {
  Briefcase,
  CheckCircle,
  Eye,
  FormInput,
  Gift,
  ListChecks,
  Settings,
  Share,
  Video,
} from "lucide-react";
import { useFormContext } from "react-hook-form";

import { FormData } from "./types";

interface SectionGridProps {
  onSelect: (section: string) => void;
}

const SectionGrid: React.FC<SectionGridProps> = ({ onSelect }) => {
  const { watch } = useFormContext<FormData>();

  // Get form values to check completion status
  const jobDetails = watch("jobDetails");
  const formDetails = watch("formDetails");
  const screeningQuestions = watch("screeningQuestions");
  const proctoringSettings = watch("proctoringSettings");
  const referralRewards = watch("referralRewards");
  const videoResume = watch("videoResume");
  const quizSettings = watch("quizSettings");

  // Check if sections are minimally completed
  const isJobDetailsComplete = !!jobDetails?.JOBINFORMATION_OGDESCRIPTION;
  const isFormDetailsComplete = true;
  const isScreeningQuestionsComplete = (screeningQuestions?.length || 0) > 0;
  const isVideoResumeComplete = videoResume?.QUIZSUBMISSION_VIDEO_RESUME
    ? !!videoResume?.QUIZSUBMISSION_VIDEO_RESUME_INSTRUCTIONS
    : true;
  const isProctoringComplete =
    proctoringSettings?.PROCTORING_ENABLED !== undefined;
  const isRefferalComplete = !!referralRewards?.REFERRAL_REWARDTYPE;
  const isQuizSettingsComplete = !!quizSettings?.QUIZ_TIMELIMIT;
  const isSubmissionSettingsComplete = true; // Always considered complete as defaults are okay

  const sections = [
    {
      id: "jobDetails",
      title: "Job Details",
      icon: <Briefcase className="h-4 w-4" />,
      description: "Configure job information and requirements",
      isComplete: isJobDetailsComplete,
    },
    {
      id: "formDetails",
      title: "Form Details",
      icon: <FormInput className="h-4 w-4" />,
      description: "Set up basic form fields and requirements",
      isComplete: isFormDetailsComplete,
    },
    {
      id: "videoResume",
      title: "Video Resume",
      icon: <FormInput className="h-4 w-4" />,
      description: "Set up video resume requirements",
      isComplete: isVideoResumeComplete,
    },
    {
      id: "screeningQuestions",
      title: "Screening Questions",
      icon: <ListChecks className="h-4 w-4" />,
      description: "Add custom questions for candidates",
      isComplete: isScreeningQuestionsComplete,
    },
    {
      id: "proctoringSettings",
      title: "Proctoring Settings",
      icon: <Eye className="h-4 w-4" />,
      description: "Configure monitoring during panda quiz",
      isComplete: isProctoringComplete,
    },
    {
      id: "referralRewards",
      title: "Referral & Rewards",
      icon: <Gift className="h-4 w-4" />,
      description: "Set up referral programs and incentives",
      isComplete: isRefferalComplete,
    },
    {
      id: "quizSettings",
      title: "Quiz Settings",
      icon: <Settings className="h-4 w-4" />,
      description: "Configure panda quiz parameters",
      isComplete: isQuizSettingsComplete,
    }
  ];

  return (
    <div className="h-full flex flex-col">

      <div className="flex-grow">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => onSelect(section.id)}
            className="flex items-center w-full p-2.5 mb-2 border rounded-md hover:bg-gray-50 hover:shadow-sm transition-all"
          >
            <div
              className={`p-2 rounded-full ${section.isComplete ? "bg-green-100 text-green-600" : "bg-primary/10 text-primary"} flex-shrink-0`}
            >
              {section.isComplete ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                section.icon
              )}
            </div>
            <div className="ml-3 text-left flex-grow">
              <h3 className="font-medium text-sm">{section.title}</h3>
              <p className="text-xs text-gray-500 line-clamp-1">
                {section.description}
              </p>
            </div>
            {section.isComplete && (
              <span className="text-xs text-green-600 mr-1">Completed</span>
            )}
          </button>
        ))}
      </div>


    </div>
  );
};

export default React.memo(SectionGrid);
